/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'
import { useDispatch } from 'react-redux'

import type { EuiButtonProps, EuiLinkButtonProps, ExclusiveUnion } from '@elastic/eui'
import { EuiButton, EuiLink } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { addToast } from '@modules/cui/Toasts'
import useExtendTrial from '@modules/billing-lib/mutations/useExtendTrial'
import useGetEntitlements from '@modules/billing-lib/queries/useGetEntitlements'
import useBillingDetails from '@modules/billing-lib/billingDetails/useBillingDetails'
import { getBillingDetailsV2 } from '@modules/billing-api/v1/callers'
import { useSaasUser } from '@modules/profile-lib/hooks'
import { useFlagsWhenLoaded } from '@modules/launchdarkly'
import usePermissions from '@modules/permissions-lib/hooks'
import useTrialUser from '@modules/billing-lib/hooks/useTrialUser'

// eslint-disable-next-line import/no-restricted-paths
import { fetchProfile } from '@/apps/userconsole/actions/profile'

import { useEbtTrialExtension } from './hooks'

type BaseProps = {
  children: React.ReactNode
  source_component_id: string
  prefixMessage?: React.ReactNode
  onSuccess?: () => any
}

type ButtonProps = BaseProps & {
  type: 'button'
  buttonProps?: EuiButtonProps
}

type LinkProps = BaseProps & {
  type: 'link'
  buttonProps?: EuiLinkButtonProps
}

type Props = ExclusiveUnion<ButtonProps, LinkProps>

const ExtendTrialButton = ({
  type,
  children,
  prefixMessage,
  source_component_id,
  buttonProps,
  onSuccess: onSuccessCallback,
}: Props) => {
  const dispatch = useDispatch()
  const { user } = useSaasUser() ?? {}
  const { data: billingData } = useBillingDetails()
  const organizationId = user?.organization_id

  const { reportTrialExtensionRequestedEbt, reportTrialExtensionSucceededEbt } =
    useEbtTrialExtension()
  const extendTrial = useExtendTrial()
  const { invalidateQueriesAfterTrialExtension } = useTrialUser()

  const { data: entitlements } = useGetEntitlements(organizationId, {
    enabled: Boolean(organizationId),
  })
  const { hasPermissions } = usePermissions([{ type: 'billing-organization', action: 'create' }]) // Billing Admin only

  const isTrialExtendable = Boolean(entitlements?.trial_extendable)
  const [, ldFlags] = useFlagsWhenLoaded()

  const checkAndReportTrialExtensionSuccess = async (baselineTrialEnd: string) => {
    if (!organizationId) {
      return
    }

    try {
      // Force fetch fresh billing data
      const freshBillingData = await getBillingDetailsV2({
        pathParameters: { organization_id: organizationId },
      })

      const currentEndMs = new Date(freshBillingData.trial?.end || '').getTime()
      const baselineMs = new Date(baselineTrialEnd).getTime()

      if (currentEndMs > baselineMs) {
        // Calculate actual extension based on new trial end date
        const nowMs = Date.now()
        const DAY_MS = 24 * 60 * 60 * 1000
        const days = Math.round((currentEndMs - nowMs) / DAY_MS)

        reportTrialExtensionSucceededEbt({
          trial_extended_with: days,
          source_component_id,
          allow_trial_extension: ldFlags?.allowTrialExtension,
        })
      }
    } catch (error) {
      console.error('Failed to fetch fresh billing data:', error)
    }
  }

  if (
    !organizationId ||
    !isTrialExtendable ||
    !hasPermissions ||
    !ldFlags?.allowTrialExtension ||
    !billingData
  ) {
    return null
  }

  const handleExtendClick = () => {
    const trialEnd = billingData.trial?.end

    if (!trialEnd) {
      return
    }

    addToast({
      title: (
        <FormattedMessage
          id='extendTrialButton.toast.requestInProgressTitle'
          defaultMessage='Request in progress'
        />
      ),
      text: (
        <FormattedMessage
          id='extendTrialButton.toast.requestInProgressMessage'
          defaultMessage='Your trial extension is being processed and will be active shortly'
        />
      ),
    })

    reportTrialExtensionRequestedEbt({
      source_component_id,
      allow_trial_extension: ldFlags?.allowTrialExtension,
    })

    extendTrial.mutate(
      { organizationId },
      {
        onSuccess: () => {
          invalidateQueriesAfterTrialExtension()
          dispatch(fetchProfile()) // refetch profile to update parent redux-based components

          setTimeout(() => {
            checkAndReportTrialExtensionSuccess(trialEnd)
          }, 1000)

          onSuccessCallback?.()
        },
        onError: (error: any) => {
          const errorMessage = error?.toString?.()

          addToast({
            title: (
              <FormattedMessage
                id='extendTrialButton.toast.requestErrorTitle'
                defaultMessage='Request failed'
              />
            ),
            text: (
              <span>
                <FormattedMessage
                  id='extendTrialButton.toast.requestErrorMessage'
                  defaultMessage='Error: '
                />
                {errorMessage ? `${String(errorMessage)}` : null}
              </span>
            ),
            color: 'danger',
            iconType: 'cross',
          })
        },
      },
    )
  }

  return (
    <Fragment>
      {prefixMessage}
      {type === 'button' ? (
        <EuiButton onClick={handleExtendClick} {...buttonProps}>
          {children}
        </EuiButton>
      ) : (
        <EuiLink onClick={handleExtendClick} {...buttonProps}>
          {children}
        </EuiLink>
      )}
    </Fragment>
  )
}

export default ExtendTrialButton
